import { Slot } from "@radix-ui/react-slot";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { cn } from "@/lib/utils";

const buttonVariants = tv({
  base: "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-60",
  variants: {
    variant: {
      primary:
        "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
      secondary:
        "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/85",
      outline:
        "border border-border bg-background text-foreground hover:bg-foreground/5 hover:text-foreground dark:hover:bg-foreground/15",
      ghost:
        "bg-transparent text-foreground hover:bg-foreground/10 hover:text-foreground dark:hover:bg-foreground/25",
      subtle: "bg-muted text-muted-foreground hover:bg-muted/80",
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      success: "bg-success text-success-foreground hover:bg-success/85",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      xs: "h-8 rounded-sm px-3 text-xs",
      sm: "h-9 rounded-sm px-3",
      fluid: "px-3 py-2 text-sm",
      md: "h-10 px-4",
      lg: "h-11 rounded-lg px-6 text-base",
      icon: "h-10 w-10",
      "icon-sm": "h-9 w-9",
    },
    emphasis: {
      solid: "",
      soft: "shadow-none",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      emphasis: "soft",
      class:
        "bg-primary/12 text-primary hover:bg-primary/20 focus-visible:ring-primary/30",
    },
    {
      variant: "secondary",
      emphasis: "soft",
      class: "bg-secondary/15 text-secondary-foreground hover:bg-secondary/25",
    },
    {
      variant: "success",
      emphasis: "soft",
      class: "bg-success/15 text-success hover:bg-success/25",
    },
    {
      variant: "destructive",
      emphasis: "soft",
      class: "bg-destructive/15 text-destructive hover:bg-destructive/25",
    },
  ],
  defaultVariants: {
    variant: "primary",
    size: "md",
    emphasis: "solid",
  },
});

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, emphasis, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component
        className={cn(buttonVariants({ variant, size, emphasis }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = "Button";

export { buttonVariants };
